/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import Box from '@material-ui/core/Box';
import isEmpty from 'lodash.isempty';
import isUndefined from 'lodash.isundefined';
import {showPendingJobs} from '@ion/applications/src/pages/devices/state';
import useHasMounted from '@ion/core/src/hooks/useHasMounted';
import {translate} from '@ion/core/src/locale/locale';
import {CRUDPermissions, DownloadJob, GenericObject} from '@ion/core/src/types/entities';
import {useExtendStyles} from '@ion/dls/src/hooks/useExtendStyles';
import {DLSTheme} from '@ion/dls/src/types/types';
import {useJob} from '@ion/query/src/hooks/useJob';
import {
  cancelTask,
  cancelTasks,
  initializeDownloadJobs,
  showDownloadJobs
} from '@/store/actions/downloadjobs';
import {ApplicationState} from '@/store/reducers';
import DownloadJobInfoPanel from './DownloadJobInfoPanel';
import DownloadJobStatusGrid from './DownloadJobStatusGrid';
import DownloadJobViewDialog from './DownloadJobViewDialog';
import TaskCancelPrompt from './TaskCancelPrompt';

export interface ViewDownloadJobStatusProps {
  downloadjobs?: DownloadJob;
  open?: boolean;
  selectedJobId?: number;
  setSelectedJobId?: (jobId: number) => void;
  showJob?: Function;
  jobsPermissions?: CRUDPermissions;
};

const styles = (theme: DLSTheme) => ({
  panelSection : {
    background : 'white'
  },
  tabStyle : {
    fontFamily    : 'Neo Sans Std',
    fontSize      : '16px',
    fontStyle     : 'normal',
    fontWeight    : 400,
    lineHeight    : '20px',
    letterSpacing : '0px',
    textAlign     : 'center',
    textTransform : 'capitalize'
  },
  contentContainer : {
    width : '100%'
  }
});

/**
 * Display View Download Job Status Editor on click on view option from grid menu
 */
export const ViewDownloadJobStatus = ({
  downloadjobs,
  showJob,
  selectedJobId,
  setSelectedJobId,
  jobsPermissions,
  open = false
}: ViewDownloadJobStatusProps) => {
  const editorStyles                  = useExtendStyles(styles);
  const isMounted                     = useHasMounted();
  const [refreshPage, setRefreshPage] = useState(false);
  const gridRefs                      = useRef<GenericObject>();
  const classes                       = editorStyles();
  const dispatch                      = useDispatch();
  const id                            = !isUndefined(selectedJobId) ? selectedJobId : downloadjobs?.id;

  const {
    job,
    refresh : onDownloadJobStatus
  } = useJob({id: id}) || {};

  const {
    result: cancelResult,
    selected,
    isEditing,
    loading,
    isSpinner,
    index,
    error
  } = useSelector(({downloadjobs: {cancelTask}}: ApplicationState) => cancelTask);

  useEffect(() => {
    if (!isMounted) {
      dispatch(initializeDownloadJobs());
    }
  }, [isMounted]);

  useEffect(() => {
    return function() {
      dispatch(cancelTasks(null, null, false));
    };
  }, []);

  useEffect(() => {
    setRefreshPage(true);
    onDownloadJobStatus();
  }, [onDownloadJobStatus]);

  const onCancel = useCallback(() => {
    showJob?.(false);
    setSelectedJobId?.(null);
    if (showPendingJobs) {
      dispatch(showPendingJobs(null, false));
    }
    dispatch(showDownloadJobs(null, false));
  }, [dispatch]);

  const onRefresh = useCallback(() => {
    setRefreshPage(true);
    onDownloadJobStatus();
  }, [onDownloadJobStatus]);

  /**
   * @private
   */
  const onDeleteConfirm = useCallback(function() {
    dispatch(cancelTask(selected, index));
  }, [selected, index]);

  /**
   * @private
   */
  const onDeleteCancel = useCallback(function() {
    dispatch(cancelTasks(selected, index, false));
  }, [selected, index]);

  const getDeleteMessage = useCallback(function() {
    if (selected && !Array.isArray(selected)) {
      const taskName = selected?.tasks?.[index]?.task?.metaData?.title;
      return translate('downloadjobs:delete.jobDeleteMessage', {label: taskName});
    }
  }, [selected, index]);

  if (!isEmpty(job)) {
    return (
      <DownloadJobViewDialog open={open}
        onCloseDialog={onCancel}
        downloadjobs={job}>
        <div>
          <DownloadJobInfoPanel downloadjobs={job} />
        </div>
        <div className={classes.contentContainer}>
          <div>
            <Box sx={{borderBottom: 1, borderColor: 'divider'}}>
              <DownloadJobStatusGrid ref={gridRefs}
                jobsPermissions={jobsPermissions}
                downloadjobs={job}
                refreshPage={refreshPage}
                gridRefresh={loading}
                dialogClose={onCancel}
                setRefreshPage={setRefreshPage}
                onRefresh={onRefresh} />
            </Box>
          </div>
        </div>
        <TaskCancelPrompt show={isEditing}
          isCancelTask={true}
          onDeleteConfirm={onDeleteConfirm}
          onDeleteCancel={onDeleteCancel}
          overrideMessage={getDeleteMessage}
          downloadjob={selected}
          index={index}
          result={cancelResult}
          loading={isSpinner}
          error={error} />
      </DownloadJobViewDialog>
    );
  }
};

export default ViewDownloadJobStatus;
